<template>
  <div v-if="node.type === '__degraded'" class="max-degraded" :style="spanStyle" role="note">
    {{ ctx.labels.degraded }}
  </div>

  <component
    :is="headingTag"
    v-else-if="node.type === 'heading'"
    v-bind="box"
    :style="spanStyle"
    :data-max-motion="motion"
    :class="['max-heading', 'max-heading--' + (node.level ?? 2)]"
  >
    <span v-if="node.eyebrow" class="max-eyebrow" style="display:block;margin-bottom:6px">{{ node.eyebrow }}</span>
    {{ node.text }}
  </component>

  <p
    v-else-if="node.type === 'text' && node.spans"
    v-bind="box"
    :class="textClass"
    :style="spanStyle"
    :data-intent="node.intent"
    :data-max-motion="motion"
  >
    <span
      v-for="(span, i) in node.spans"
      :key="i"
      :class="{ 'max-span--strong': span.strong, 'max-span--mono': span.mono, 'max-span--muted': span.tone === 'muted' }"
      :data-intent="span.intent"
    >{{ span.text }}</span>
  </p>

  <div v-else-if="node.type === 'text'" v-bind="box" :style="spanStyle" :data-max-motion="motion">
    <p
      v-for="(paragraph, i) in paragraphs"
      :key="i"
      :class="textClass"
      :data-intent="node.intent"
      :style="node.truncate ? undefined : 'white-space: pre-line'"
    >{{ paragraph }}</p>
  </div>

  <component
    :is="node.ordered ? 'ol' : 'ul'"
    v-else-if="node.type === 'list' && node.template === undefined"
    class="max-list"
    :style="spanStyle"
    :data-max-motion="motion"
  >
    <li v-for="(item, i) in node.items" :key="i">{{ item }}</li>
  </component>

  <div v-else-if="node.type === 'list'" v-bind="box" :style="spanStyle">
    <p v-if="!node.items.length" class="max-datatable__empty">{{ node.empty || ctx.labels.ui.noRows }}</p>
    <component
      :is="node.ordered ? 'ol' : 'ul'"
      v-else
      class="max-itemlist"
      :data-divided="node.divided === false ? undefined : 'true'"
      :data-density="node.density ?? 'normal'"
    >
      <li
        v-for="item in node.items"
        :key="item.key"
        class="max-itemlist__item"
        :data-selected="item.selected ? 'true' : undefined"
        :data-clickable="listClickable ? 'true' : undefined"
        :tabindex="listClickable ? 0 : undefined"
        :aria-current="item.selected ? 'true' : undefined"
        @click="selectItem($event, item)"
        @keydown.enter.self="selectItem($event, item)"
        @keydown.space.self.prevent="selectItem($event, item)"
      >
        <template v-if="node.template">
          <MaxNode v-for="(child, i) in item.children" :key="(child as any).id ?? i" :node="child" :index="i" />
        </template>
        <template v-else>{{ item.text }}</template>
      </li>
    </component>
  </div>

  <aside
    v-else-if="node.type === 'callout'"
    v-bind="box"
    class="max-callout"
    :data-intent="node.intent ?? 'info'"
    :style="spanStyle"
    :data-max-motion="motion"
  >
    <p v-if="node.title" class="max-callout__title">{{ node.title }}</p>
    <p style="white-space: pre-line">{{ node.text }}</p>
  </aside>

  <hr v-else-if="node.type === 'divider'" v-bind="box" class="max-divider" :style="spanStyle">

  <MaxMetric v-else-if="node.type === 'metric'" v-bind="box" :node="node" :style="spanStyle" :data-max-motion="motion" />

  <MaxScore v-else-if="node.type === 'score'" v-bind="box" :node="node" :style="spanStyle" :data-max-motion="motion" />

  <MaxFindings
    v-else-if="node.type === 'finding'"
    :items="[node]"
    group-by="none"
    :filterable="false"
    :style="spanStyle"
    :data-max-motion="motion"
  />

  <MaxFindings
    v-else-if="node.type === 'findings'"
    :items="node.items"
    :group-by="node.groupBy ?? 'severity'"
    :title="node.title"
    :style="spanStyle"
    :data-max-motion="motion"
  />

  <MaxTable v-else-if="node.type === 'table'" v-bind="box" :node="node" :style="spanStyle" :data-max-motion="motion" />

  <MaxChart v-else-if="node.type === 'chart'" v-bind="box" :node="node" :style="spanStyle" :data-max-motion="motion" />

  <MaxDiagram v-else-if="node.type === 'diagram'" :node="node" :style="spanStyle" :data-max-motion="motion" />

  <MaxRequest v-else-if="node.type === 'request'" :node="node" :style="spanStyle" :data-max-motion="motion" />

  <MaxTabs v-else-if="node.type === 'tabs'" v-bind="box" :node="node" :style="spanStyle" :data-max-motion="motion" />

  <template v-else-if="isControl">
    <component :is="hostControl" v-if="iface && hostControl" v-bind="box" :node="node" :style="spanStyle" />
    <MaxControl v-else-if="iface" v-bind="box" :node="node" :style="spanStyle" />
    <div v-else class="max-degraded" :style="spanStyle" role="note">{{ ctx.labels.degraded }}</div>
  </template>

  <p v-else-if="node.type === 'link'" v-bind="box" :style="spanStyle" :data-max-motion="motion">
    <a
      v-if="safeHref"
      :href="safeHref"
      target="_blank"
      rel="noopener noreferrer nofollow"
      :class="['max-link', node.style === 'button' ? 'max-link--button' : '']"
    >
      {{ node.label }}
      <span class="max-sr-only">({{ ctx.labels.opensInNewTab }})</span>
      <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true"><path d="M4 2h6v6M10 2 3 9" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" /></svg>
    </a>
    <span v-else>{{ node.label }}</span>
  </p>

  <section
    v-else-if="node.type === 'section'"
    v-bind="box"
    :class="sectionClass"
    :style="spanStyle"
    :data-max-motion="motion"
    :aria-labelledby="node.title ? headingId : undefined"
  >
    <header v-if="node.title || node.description || node.eyebrow" class="max-panel__header" style="display:block">
      <p v-if="node.eyebrow" class="max-eyebrow">{{ node.eyebrow }}</p>
      <h2 v-if="node.title" :id="headingId" class="max-panel__title">{{ node.title }}</h2>
      <p v-if="node.description" class="max-panel__description">{{ node.description }}</p>
    </header>
    <div data-max-layout="stack" :data-max-stagger="node.motion === 'stagger' ? 'true' : undefined">
      <MaxNode v-for="(child, i) in node.children" :key="(child as any).id ?? i" :node="child" :index="i" />
    </div>
  </section>

  <div
    v-else-if="isLayout"
    v-bind="{ ...box, ...layout.attrs }"
    :style="{ ...layout.style, ...spanStyle }"
    :data-max-stagger="node.motion === 'stagger' ? 'true' : undefined"
  >
    <MaxNode
      v-for="(child, i) in node.children"
      :key="(child as any).id ?? i"
      :node="child"
      :index="i"
      :parent-columns="node.type === 'grid' ? (node.columns ?? 'auto') : undefined"
    />
  </div>

  <article
    v-else-if="node.type === 'card'"
    v-bind="box"
    class="max-card"
    :data-tone="node.tone ?? 'surface'"
    :data-intent="cardIntent"
    :data-pad="node.pad ?? 'md'"
    :data-selected="node.selected ? 'true' : undefined"
    :data-clickable="cardClickable ? 'true' : undefined"
    :tabindex="cardClickable ? 0 : undefined"
    :aria-current="node.selected ? 'true' : undefined"
    :aria-labelledby="node.title ? headingId : undefined"
    :style="{ ...spanStyle, '--max-layout-gap': gapValue(node.gap, 'sm') }"
    :data-max-motion="motion"
    @click="clickCard"
    @keydown.enter.self="clickCard"
    @keydown.space.self.prevent="clickCard"
  >
    <header v-if="node.title || node.eyebrow" class="max-card__header">
      <p v-if="node.eyebrow" class="max-eyebrow">{{ node.eyebrow }}</p>
      <h3 v-if="node.title" :id="headingId" class="max-card__title">{{ node.title }}</h3>
    </header>
    <MaxNode v-for="(child, i) in node.children" :key="(child as any).id ?? i" :node="child" :index="i" />
  </article>

  <div v-else-if="node.type === 'scroll'" v-bind="box" class="max-scroll" :data-height="node.height ?? 'md'" :style="spanStyle" tabindex="0">
    <MaxNode v-for="(child, i) in node.children" :key="(child as any).id ?? i" :node="child" :index="i" />
  </div>

  <div v-else-if="node.type === 'sticky'" v-bind="box" class="max-sticky" :data-edge="node.edge ?? 'top'" :style="spanStyle">
    <MaxNode v-for="(child, i) in node.children" :key="(child as any).id ?? i" :node="child" :index="i" />
  </div>

  <details v-else-if="node.type === 'disclosure'" v-bind="box" class="max-disclosure" :open="node.initiallyOpen || undefined" :style="spanStyle">
    <summary class="max-disclosure__summary">
      <span class="max-disclosure__title">{{ node.title }}</span>
      <span v-if="node.summary" class="max-disclosure__meta">{{ node.summary }}</span>
      <MaxIcon name="chevron-down" class="max-disclosure__chevron" />
    </summary>
    <div class="max-disclosure__body">
      <MaxNode v-for="(child, i) in node.children" :key="(child as any).id ?? i" :node="child" :index="i" />
    </div>
  </details>

  <div v-else-if="node.type === 'empty'" v-bind="box" class="max-empty" :style="spanStyle" role="status">
    <MaxIcon v-if="node.icon" :name="node.icon" size="lg" class="max-empty__icon" />
    <p class="max-empty__title">{{ node.title }}</p>
    <p v-if="node.text" class="max-empty__text">{{ node.text }}</p>
    <div v-if="node.children?.length" class="max-empty__actions">
      <MaxNode v-for="(child, i) in node.children" :key="(child as any).id ?? i" :node="child" :index="i" />
    </div>
  </div>

  <div v-else-if="node.type === 'timeline'" v-bind="box" :style="spanStyle">
    <p v-if="!node.items.length" class="max-datatable__empty">{{ node.empty || ctx.labels.ui.noRows }}</p>
    <ol v-else class="max-timeline">
      <li v-for="item in node.items" :key="item.key" class="max-timeline__item" :data-intent="item.intent || undefined">
        <span class="max-timeline__time">{{ item.time }}</span>
        <span class="max-timeline__rail" aria-hidden="true"><span class="max-timeline__marker" /><span class="max-timeline__line" /></span>
        <div class="max-timeline__body">
          <MaxNode v-for="(child, i) in item.children" :key="(child as any).id ?? i" :node="child" :index="i" />
        </div>
      </li>
    </ol>
  </div>

  <span
    v-else-if="node.type === 'badge'"
    v-bind="box"
    class="max-badge"
    :data-intent="node.intent"
    :data-variant="node.variant ?? 'soft'"
    :data-size="node.size ?? 'md'"
    :data-mono="node.mono ? 'true' : undefined"
    :style="spanStyle"
  ><MaxIcon v-if="node.icon" :name="node.icon" size="sm" />{{ node.text }}</span>

  <span v-else-if="node.type === 'icon'" v-bind="box" class="max-icon-wrap" :data-intent="node.intent" :style="spanStyle">
    <MaxIcon :name="node.name" :label="node.label" :size="node.size ?? 'md'" />
  </span>

  <span
    v-else-if="node.type === 'avatar'"
    v-bind="box"
    class="max-avatar"
    :data-intent="node.intent ?? 'accent'"
    :data-size="node.size ?? 'md'"
    role="img"
    :aria-label="node.name"
    :title="node.name"
    :style="spanStyle"
  >{{ avatarInitials }}</span>

  <dl v-else-if="node.type === 'keyvalue'" v-bind="box" class="max-kv" :data-layout="node.layout ?? 'stacked'" :style="spanStyle">
    <div v-for="(item, i) in node.items" :key="i" class="max-kv__item">
      <dt class="max-kv__label">{{ item.label }}</dt>
      <dd class="max-kv__value" :data-mono="item.mono ? 'true' : undefined" :data-intent="item.intent">{{ item.value === '' ? '–' : item.value }}</dd>
    </div>
  </dl>

  <div v-else-if="node.type === 'progress'" v-bind="box" class="max-progress" :data-variant="node.variant ?? 'bar'" :data-intent="node.intent ?? 'accent'" :style="spanStyle">
    <span v-if="node.label" :id="headingId" class="max-progress__label">{{ node.label }}</span>
    <svg
      v-if="node.variant === 'ring'"
      class="max-progress__ring"
      viewBox="0 0 36 36"
      role="progressbar"
      :aria-valuenow="node.value"
      aria-valuemin="0"
      :aria-valuemax="node.max"
      :aria-labelledby="node.label ? headingId : undefined"
    >
      <circle cx="18" cy="18" r="15.5" fill="none" stroke-width="4" class="max-progress__track" />
      <circle cx="18" cy="18" r="15.5" fill="none" stroke-width="4" class="max-progress__fill" pathLength="100" :stroke-dasharray="ratio * 100 + ' 100'" transform="rotate(-90 18 18)" />
    </svg>
    <span
      v-else
      class="max-progress__bar"
      role="progressbar"
      :aria-valuenow="node.value"
      aria-valuemin="0"
      :aria-valuemax="node.max"
      :aria-labelledby="node.label ? headingId : undefined"
    ><span class="max-progress__fill" :style="{ width: ratio * 100 + '%' }" /></span>
    <span v-if="progressText" class="max-progress__value">{{ progressText }}</span>
  </div>

  <img
    v-else-if="node.type === 'image' && imageSrc"
    v-bind="box"
    class="max-image"
    :data-ratio="node.ratio ?? 'auto'"
    :src="imageSrc"
    :alt="node.alt ?? ''"
    loading="lazy"
    :style="spanStyle"
  >

  <div v-else-if="node.type === 'spacer'" v-bind="box" class="max-spacer" :data-size="node.size ?? 'md'" aria-hidden="true" />
</template>

<script setup lang="ts">
import { computed, useId } from 'vue'
import { boxAttrs, resolveLayout, resolveSpan, GAPS, type LayoutContainer } from '../core/layout.ts'
import { HTTPS_URL } from '../core/document.ts'
import { CONTROL_TYPES } from '../core/interface.ts'
import { initials } from '../core/icons.ts'
import { useMaxContext, useMaxHostControl, useMaxInterface } from '../runtime/context.ts'
import MaxIcon from './MaxIcon.vue'

defineOptions({ name: 'MaxNode' })

const props = defineProps<{
  node: any
  index?: number
  parentColumns?: number | 'auto' | Record<string, number>
}>()

const LAYOUTS = ['grid', 'stack', 'split', 'row', 'sidebar']
const INTENTS = ['neutral', 'accent', 'positive', 'warning', 'negative', 'info']
/** Inside a clickable card or list item these keep their own click */
const INTERACTIVE = 'a, button, input, select, textarea, label, summary, [role="button"], [tabindex]'

const ctx = useMaxContext()
const iface = useMaxInterface()
const isControl = computed(() => (CONTROL_TYPES as readonly string[]).includes(props.node.type))
const isLayout = computed(() => LAYOUTS.includes(props.node.type))
const hostControl = useMaxHostControl(() => props.node.type)
const headingId = 'max-h-' + useId()

const box = computed(() => boxAttrs(props.node))
const spanStyle = computed(() => props.parentColumns !== undefined ? resolveSpan(props.node.span, typeof props.parentColumns === 'object' ? undefined : props.parentColumns) : {})
const motion = computed(() => props.node.motion && props.node.motion !== 'none' ? props.node.motion : undefined)
const headingTag = computed(() => 'h' + Math.min(4, (props.node.level ?? 2) + 1))
const paragraphs = computed(() => String(props.node.text ?? '').split(/\n{2,}/).map((p: string) => p.trim()).filter(Boolean))
const safeHref = computed(() => typeof props.node.href === 'string' && HTTPS_URL.test(props.node.href) ? props.node.href : null)
const layout = computed(() => resolveLayout(props.node as LayoutContainer))
const sectionClass = computed(() => {
  const surface = props.node.surface ?? 'panel'
  if (surface === 'plain') return ['max-section']
  if (surface === 'sunken') return ['max-panel', 'max-panel--sunken']
  return ['max-panel']
})
const textClass = computed(() => [
  'max-text',
  props.node.tone && props.node.tone !== 'default' ? 'max-text--' + props.node.tone : '',
  props.node.size ? 'max-text--size-' + props.node.size : '',
  props.node.weight ? 'max-text--' + props.node.weight : '',
  props.node.mono ? 'max-text--mono' : '',
  props.node.align ? 'max-text--' + props.node.align : '',
  props.node.truncate ? 'max-text--truncate' : ''
])

const gapValue = (gap: unknown, fallback: string) => {
  const value = typeof gap === 'string' && (GAPS as readonly string[]).includes(gap) ? gap : fallback
  return value === 'none' ? '0px' : `var(--max-gap-${value})`
}

function fromInteractive (event: Event): boolean {
  const target = event.target as HTMLElement | null
  const current = event.currentTarget as HTMLElement | null
  const hit = target?.closest?.(INTERACTIVE)
  return !!hit && hit !== current && !!current?.contains(hit)
}

function openActions (open: { to: string, id?: unknown }) {
  return [{ do: 'navigate', to: open.to, id: open.id ?? { var: 'item.id' } }]
}

const cardIntent = computed(() => INTENTS.includes(props.node.tone) ? props.node.tone : undefined)
const cardClickable = computed(() => !!(props.node.on?.click || props.node.open))
function clickCard (event: Event) {
  if (!cardClickable.value || !iface || fromInteractive(event)) return
  const scope = props.node.scope ?? {}
  if (props.node.on?.click) iface.run(props.node.on.click, scope)
  else iface.run(openActions(props.node.open), scope)
}

const listClickable = computed(() => !!(props.node.on?.select || props.node.open))
function selectItem (event: Event, item: { scope?: Record<string, unknown> }) {
  if (!listClickable.value || !iface || fromInteractive(event)) return
  if (props.node.on?.select) iface.run(props.node.on.select, item.scope ?? {})
  else iface.run(openActions(props.node.open), item.scope ?? {})
}

const avatarInitials = computed(() => initials(String(props.node.name ?? '')))
const ratio = computed(() => {
  const max = Number(props.node.max) || 100
  return Math.max(0, Math.min(1, (Number(props.node.value) || 0) / max))
})
const progressText = computed(() => {
  if (props.node.show === 'fraction') return `${props.node.value}/${props.node.max}`
  if (props.node.show === 'percent') return new Intl.NumberFormat(ctx.locale.replace('_', '-'), { style: 'percent' }).format(ratio.value)
  return ''
})
const imageSrc = computed(() => {
  if (props.node.type !== 'image' || props.node.id === null || props.node.id === undefined || !ctx.imageUrl) return null
  return ctx.imageUrl(props.node.source, props.node.id)
})
</script>
